import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, Linking, Platform, RefreshControl, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { AttendanceComments } from '../../components/AttendanceComments';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ResponsiveSplit } from '../../components/ResponsiveSplit';
import { ScreenHeader } from '../../components/ScreenHeader';
import { TrackingMap } from '../../components/TrackingMap';
import { TrackingMapLegend, trackingMapColors } from '../../components/TrackingMapLegend';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useLocationTrackerStatus } from '../../hooks/useLocationTracking';
import { getLocationTracker } from '../../services/locationTracker';
import { useRefreshOnFocus } from '../../hooks/useRefreshOnFocus';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { normalizeApiError } from '../../api/apiError';
import type { TrackerStatus } from '../../services/locationTracker';
import { getTrackingDetails } from '../../services/locationTrackingService';
import type {
  TrackingDetails,
  TrackingPlace,
  TrackingTimelineItem,
  TrackingVisitStatus,
} from '../../types/locationTracking';
import { attendanceLocationModeLabel } from '../../utils/attendanceLocationMode';
import { formatAttendanceDate, formatDateTime, formatTime } from '../../utils/dateTime';
import {
  TRACKING_STATUS_LABELS as STATUS_LABELS,
  formatCoordinates,
  formatDistance,
  formatStayDuration,
} from '../../utils/locationTracking';

type Props = NativeStackScreenProps<RootStackParamList, 'LocationTrackingDetails'>;

const TIMELINE_TITLES: Record<TrackingTimelineItem['type'], string> = {
  CHECK_IN: 'Checked in · Start location',
  STAY: 'Long stay',
  COMMENT: 'Comment',
  SIGNAL_GAP: 'No location received',
  CHECK_OUT: 'Checked out · End location',
};

function statusTone(status: TrackingVisitStatus, palette: ColorPalette) {
  if (status === 'ACTIVE' || status === 'STARTING') return palette.success;
  if (status === 'COMPLETED') return palette.info;
  return palette.warning;
}

function placeText(item: { address: string | null; latitude: number | null; longitude: number | null }) {
  return item.address ?? formatCoordinates(item.latitude, item.longitude);
}

/** Device tracker problems the employee can fix, shown only on their own live visit. */
type TrackerNotice = { message: string; settings: 'app' | 'location' | null; retry: boolean };

function trackerNotice(status: TrackerStatus): TrackerNotice | null {
  switch (status.state) {
    case 'permission_denied':
      return {
        message: 'Location permission is off, so your route is not being recorded. Allow location access to resume tracking.',
        settings: 'app',
        retry: true,
      };
    case 'error':
      return {
        message: status.error ?? 'Live tracking stopped. Try again.',
        settings: null,
        retry: true,
      };
    case 'starting':
      return null;
    case 'permission_blocked':
      return {
        message:
          Platform.OS === 'ios'
            ? 'Location access is turned off for this app. Open Settings, choose Location, and select "While Using the App" or "Always".'
            : 'Location permission was denied. Open Settings and allow Location for this app to resume tracking.',
        settings: 'app',
        retry: false,
      };
    case 'location_disabled':
      return {
        message: 'Location services are turned off. Turn them on to resume tracking.',
        settings: Platform.OS === 'android' ? 'location' : 'app',
        retry: true,
      };
    case 'unavailable':
      return { message: 'Live tracking is not available on the server yet.', settings: null, retry: false };
    default:
      if (status.offline && status.pendingPoints > 0) {
        return {
          message: `You are offline. ${status.pendingPoints} location update(s) are saved on this device and will upload automatically.`,
          settings: null,
          retry: false,
        };
      }
      return null;
  }
}

function Metric({ label, value, palette }: { label: string; value: string; palette: ColorPalette }) {
  return (
    <View style={styles.metric}>
      <Text style={[typography.caption, { color: palette.textMuted }]}>{label}</Text>
      <Text style={[typography.body, styles.metricValue, { color: palette.textPrimary }]}>{value}</Text>
    </View>
  );
}

function PlaceCard({
  title,
  place,
  emptyText,
  tone,
  palette,
}: {
  title: string;
  place: TrackingPlace | null;
  emptyText: string;
  tone: string;
  palette: ColorPalette;
}) {
  return (
    <AppCard style={styles.card}>
      <View style={styles.rowCenter}>
        <View style={[styles.dot, { backgroundColor: tone }]} />
        <Text style={[typography.sectionTitle, { color: palette.textPrimary }]}>{title}</Text>
      </View>
      {place ? (
        <>
          <Text style={[typography.body, styles.strong, { color: palette.textPrimary }]}>
            {formatDateTime(place.at)}
          </Text>
          <Text style={[typography.body, { color: palette.textSecondary }]}>{placeText(place)}</Text>
          {place.latitude !== null ? (
            <Text style={[typography.caption, { color: palette.textMuted }]}>
              {formatCoordinates(place.latitude, place.longitude)}
              {place.accuracyMeters !== null ? ` · ±${Math.round(place.accuracyMeters)} m` : ''}
            </Text>
          ) : null}
        </>
      ) : (
        <Text style={[typography.body, { color: palette.textSecondary }]}>{emptyText}</Text>
      )}
    </AppCard>
  );
}

function TimelineEntry({
  item,
  last,
  palette,
}: {
  item: TrackingTimelineItem;
  last: boolean;
  palette: ColorPalette;
}) {
  const colors = trackingMapColors(palette);
  const tone =
    item.type === 'CHECK_IN'
      ? colors.checkIn
      : item.type === 'STAY'
        ? colors.stay
        : item.type === 'COMMENT'
          ? colors.comment
          : item.type === 'CHECK_OUT'
            ? colors.checkOut
            : palette.textMuted;
  const title =
    item.type === 'STAY' && item.stayNumber
      ? `Long stay ${item.stayNumber} · ${formatStayDuration(item.durationMinutes)}${item.ongoing ? ' (still here)' : ''}`
      : item.type === 'SIGNAL_GAP'
        ? `${TIMELINE_TITLES.SIGNAL_GAP} · ${formatStayDuration(item.durationMinutes)}`
        : TIMELINE_TITLES[item.type];
  const time =
    item.until && item.type !== 'COMMENT'
      ? `${formatTime(item.at)} – ${formatTime(item.until)}`
      : item.type === 'STAY' && item.ongoing
        ? `Since ${formatTime(item.at)}`
        : formatTime(item.at);
  return (
    <View style={styles.timelineRow} testID={`timeline-${item.id}`}>
      <View style={styles.rail}>
        <View style={[styles.node, { backgroundColor: tone, borderColor: palette.surface }]} />
        {!last ? <View style={[styles.railLine, { backgroundColor: palette.border }]} /> : null}
      </View>
      <View style={styles.timelineBody}>
        <Text style={[typography.caption, { color: palette.textMuted }]}>{time}</Text>
        <Text style={[typography.body, styles.strong, { color: palette.textPrimary }]}>{title}</Text>
        {item.type === 'COMMENT' && item.comment ? (
          <Text style={[typography.body, { color: palette.textPrimary }]}>“{item.comment}”</Text>
        ) : null}
        {item.type === 'SIGNAL_GAP' ? (
          <Text style={[typography.caption, { color: palette.textSecondary }]}>
            GPS signal or network was unavailable during this time.
          </Text>
        ) : item.latitude !== null ? (
          <Text style={[typography.caption, { color: palette.textSecondary }]}>{placeText(item)}</Text>
        ) : (
          <Text style={[typography.caption, { color: palette.textMuted }]}>Location not available</Text>
        )}
      </View>
    </View>
  );
}

export function LocationTrackingDetailsScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const layout = useResponsiveLayout();
  const trackerStatus = useLocationTrackerStatus();
  const attendanceId = Number(route.params?.attendanceId);
  const validId = Number.isInteger(attendanceId) && attendanceId > 0;
  const [details, setDetails] = useState<TrackingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const requestId = useRef(0);

  const load = useCallback(
    async (mode: 'initial' | 'refresh' | 'silent' = 'initial') => {
      if (!validId) {
        setError('Tracking details were not found.');
        setLoading(false);
        return;
      }
      const current = ++requestId.current;
      if (mode === 'refresh') setRefreshing(true);
      try {
        const next = await getTrackingDetails(attendanceId);
        if (!mounted.current || current !== requestId.current) return;
        setDetails(next);
        setError(null);
      } catch (caught) {
        if (!mounted.current || current !== requestId.current) return;
        // Keep showing the last good data on a failed background refresh.
        if (mode !== 'silent') setError(normalizeApiError(caught).message);
      } finally {
        if (mounted.current && current === requestId.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [attendanceId, validId],
  );

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
    };
  }, [load]);
  useRefreshOnFocus(() => void load('silent'));

  // Live visits refresh on the server-configured interval, only while this
  // screen is visible and the app is in the foreground.
  const liveSeconds = details?.active ? details.settings.liveRefreshSeconds : null;
  useEffect(() => {
    if (!liveSeconds) return undefined;
    const timer = setInterval(() => {
      if (AppState.currentState !== 'active') return;
      if (navigation.isFocused && !navigation.isFocused()) return;
      void load('silent');
    }, liveSeconds * 1000);
    return () => clearInterval(timer);
  }, [liveSeconds, load, navigation]);

  const isOwn = details ? details.employee.employeeId === auth.user?.employeeId : false;
  const notice = isOwn && details?.active ? trackerNotice(trackerStatus) : null;
  const mapHeight = layout.isWide ? 460 : layout.isTablet ? 380 : 280;
  const hasMapData = Boolean(
    details &&
      (details.route.length > 0 ||
        details.checkIn?.latitude != null ||
        details.checkOut?.latitude != null ||
        details.stays.length > 0 ||
        details.comments.some(comment => comment.latitude !== null)),
  );
  const colors = trackingMapColors(palette);
  const typeLabel = useMemo(
    () => attendanceLocationModeLabel(details?.visit.attendanceSource) ?? 'Not available',
    [details?.visit.attendanceSource],
  );

  const header = (
    <ScreenHeader title="Tracking Details" onBack={navigation.goBack} />
  );

  return (
    <PermissionGuard permission={PERMISSIONS.ATTENDANCE_VIEW} fallback={<UnauthorizedScreen />}>
      {loading && !details ? (
        <AppScreen contentStyle={styles.center}>
          {header}
          <AppLoader message="Loading tracking details" />
        </AppScreen>
      ) : error && !details ? (
        <AppScreen contentStyle={styles.center}>
          {header}
          <ErrorState message={error} onRetry={() => void load()} />
        </AppScreen>
      ) : details ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => void load('refresh')}
              tintColor={palette.primary}
            />
          }
        >
          {header}
          <View style={styles.body}>
            <AppCard style={styles.card}>
              <View style={styles.heroTop}>
                <View style={styles.flexText}>
                  <Text style={[typography.sectionTitle, { color: palette.textPrimary }]} numberOfLines={2}>
                    {details.employee.displayName ?? 'Employee'}
                  </Text>
                  <Text style={[typography.caption, { color: palette.textSecondary }]}>
                    {[details.employee.employeeNumber, typeLabel].filter(Boolean).join(' · ')}
                  </Text>
                </View>
                <View
                  accessibilityLabel={`Status: ${STATUS_LABELS[details.visit.status]}`}
                  style={[styles.pill, { backgroundColor: `${statusTone(details.visit.status, palette)}22` }]}
                >
                  <View style={[styles.dot, { backgroundColor: statusTone(details.visit.status, palette) }]} />
                  <Text style={[typography.caption, styles.strong, { color: palette.textPrimary }]}>
                    {STATUS_LABELS[details.visit.status]}
                  </Text>
                </View>
              </View>
              <View style={styles.metrics}>
                <Metric label="Date" value={formatAttendanceDate(details.visit.attendanceDate)} palette={palette} />
                <Metric label={details.active ? 'Duration so far' : 'Visit duration'} value={formatStayDuration(details.summary.durationMinutes)} palette={palette} />
                <Metric label="Distance travelled" value={formatDistance(details.summary.distanceMeters)} palette={palette} />
                <Metric label="Long stays" value={String(details.summary.stayCount)} palette={palette} />
                <Metric label="Comments" value={String(details.summary.commentCount)} palette={palette} />
                <Metric
                  label="Last location"
                  value={details.summary.lastLocationAt ? formatDateTime(details.summary.lastLocationAt) : 'Not received yet'}
                  palette={palette}
                />
              </View>
              {details.visit.visitId ? (
                <Text style={[typography.caption, { color: palette.textMuted }]} selectable>
                  Visit ID: {details.visit.visitId}
                </Text>
              ) : null}
            </AppCard>

            {!details.eligible ? (
              <AppCard style={styles.card}>
                <Text style={[typography.body, { color: palette.textSecondary }]}>
                  Live tracking applies only to client visit attendance. This day shows its check-in and check-out only.
                </Text>
              </AppCard>
            ) : !details.trackingAvailable ? (
              <AppCard style={styles.card}>
                <Text style={[typography.body, { color: palette.textSecondary }]}>
                  Route recording is not enabled on the server yet. Check-in, check-out and comments are shown.
                </Text>
              </AppCard>
            ) : null}

            {notice ? (
              <AppCard style={[styles.card, { borderColor: palette.warning, borderWidth: 1 }]}>
                <Text accessibilityRole="alert" style={[typography.body, { color: palette.textPrimary }]}>
                  {notice.message}
                </Text>
                {notice.retry ? (
                  <AppButton
                    title="Retry tracking"
                    onPress={() => void getLocationTracker().retry()}
                  />
                ) : null}
                {notice.settings ? (
                  <AppButton
                    title="Open Settings"
                    variant="secondary"
                    onPress={() => {
                      void (notice.settings === 'location'
                        ? Linking.sendIntent('android.settings.LOCATION_SOURCE_SETTINGS')
                        : Linking.openSettings()
                      ).catch(() => Linking.openSettings());
                    }}
                  />
                ) : null}
              </AppCard>
            ) : null}

            <AppCard style={styles.card}>
              <Text style={[typography.sectionTitle, { color: palette.textPrimary }]}>Route map</Text>
              {hasMapData ? (
                <>
                  <TrackingMap details={details} height={mapHeight} />
                  <TrackingMapLegend showCurrent={Boolean(details.active && details.currentLocation)} />
                </>
              ) : (
                <Text style={[typography.body, { color: palette.textSecondary }]}>
                  {details.active
                    ? 'Waiting for the first location update.'
                    : 'No locations were recorded for this visit.'}
                </Text>
              )}
            </AppCard>

            <ResponsiveSplit>
              <View>
                {details.active ? (
                  <PlaceCard
                    title="Current location"
                    place={details.currentLocation}
                    emptyText="Waiting for the first location update."
                    tone={colors.current}
                    palette={palette}
                  />
                ) : null}
                <PlaceCard
                  title="Check-in location"
                  place={details.checkIn}
                  emptyText="Check-in was not recorded."
                  tone={colors.checkIn}
                  palette={palette}
                />
                <PlaceCard
                  title="Check-out location"
                  place={details.checkOut}
                  emptyText={details.active ? 'Not checked out yet.' : 'Check-out was not recorded.'}
                  tone={colors.checkOut}
                  palette={palette}
                />
                <AppCard style={styles.card}>
                  <Text style={[typography.sectionTitle, { color: palette.textPrimary }]}>Long-stay locations</Text>
                  <Text style={[typography.caption, { color: palette.textMuted }]}>
                    Places where the employee stayed within {details.settings.longStayRadiusMeters} m for at least {details.settings.longStayMinMinutes} minutes.
                  </Text>
                  {details.stays.length === 0 ? (
                    <Text style={[typography.body, { color: palette.textSecondary }]}>No long stays recorded.</Text>
                  ) : (
                    details.stays.map(stay => (
                      <View key={stay.stayNumber} style={[styles.listItem, { borderColor: palette.border }]}>
                        <View style={styles.rowBetween}>
                          <Text style={[typography.body, styles.strong, { color: palette.textPrimary }]}>
                            Stay {stay.stayNumber}
                          </Text>
                          <Text style={[typography.body, styles.strong, { color: palette.warning }]}>
                            {formatStayDuration(stay.durationMinutes)}
                          </Text>
                        </View>
                        <Text style={[typography.caption, { color: palette.textSecondary }]}>
                          Arrived {formatTime(stay.arrivedAt)} ·{' '}
                          {stay.ongoing ? 'Still here' : `Left ${formatTime(stay.departedAt)}`}
                          {stay.includesCheckIn ? ' · Check-in place' : ''}
                          {stay.includesCheckOut ? ' · Check-out place' : ''}
                        </Text>
                        <Text style={[typography.body, { color: palette.textPrimary }]}>{placeText(stay)}</Text>
                      </View>
                    ))
                  )}
                </AppCard>
                {isOwn && details.active ? (
                  <AppCard style={styles.card}>
                    <Text style={[typography.sectionTitle, { color: palette.textPrimary }]}>Visit comments</Text>
                    <Text style={[typography.caption, { color: palette.textMuted }]}>
                      Each comment saves your current location and time.
                    </Text>
                    <AttendanceComments
                      attendanceId={details.visit.attendanceId}
                      canMark={auth.hasPermission(PERMISSIONS.ATTENDANCE_MARK)}
                      disabled={false}
                      refreshKey={String(details.visit.attendanceId)}
                      showList={false}
                      onAdded={() => void load('silent')}
                    />
                  </AppCard>
                ) : null}
              </View>
              <AppCard style={styles.card}>
                <Text style={[typography.sectionTitle, { color: palette.textPrimary }]}>Visit timeline</Text>
                {details.timeline.length === 0 ? (
                  <Text style={[typography.body, { color: palette.textSecondary }]}>No activity recorded yet.</Text>
                ) : (
                  <View accessibilityLabel="Visit timeline">
                    {details.timeline.map((item, index) => (
                      <TimelineEntry
                        key={item.id}
                        item={item}
                        last={index === details.timeline.length - 1}
                        palette={palette}
                      />
                    ))}
                  </View>
                )}
                {details.active ? (
                  <Text style={[typography.caption, { color: palette.textMuted }]}>
                    Updates every {details.settings.liveRefreshSeconds} seconds while the visit is live.
                  </Text>
                ) : null}
              </AppCard>
            </ResponsiveSplit>
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  center: { justifyContent: 'center', gap: spacing.md },
  body: { marginTop: spacing.lg, gap: spacing.md, minWidth: 0 },
  card: { gap: spacing.sm, marginBottom: spacing.md, minWidth: 0 },
  heroTop: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: spacing.sm },
  flexText: { flexGrow: 1, flexShrink: 1, minWidth: 160, gap: 2 },
  pill: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingHorizontal: spacing.md, paddingVertical: spacing.xs, borderRadius: 999 },
  metrics: { flexDirection: 'row', flexWrap: 'wrap', rowGap: spacing.md, columnGap: spacing.lg },
  metric: { minWidth: 130, flexGrow: 1, flexBasis: '28%', gap: 2 },
  metricValue: { fontWeight: '700' },
  strong: { fontWeight: '700' },
  dot: { width: 10, height: 10, borderRadius: 5 },
  rowCenter: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  rowBetween: { flexDirection: 'row', justifyContent: 'space-between', gap: spacing.sm, flexWrap: 'wrap' },
  listItem: { borderTopWidth: StyleSheet.hairlineWidth, paddingTop: spacing.sm, gap: 2 },
  timelineRow: { flexDirection: 'row', gap: spacing.md },
  rail: { width: 16, alignItems: 'center' },
  node: { width: 14, height: 14, borderRadius: 7, borderWidth: 2, marginTop: 2 },
  railLine: { width: 2, flex: 1, marginVertical: 2 },
  timelineBody: { flex: 1, minWidth: 0, paddingBottom: spacing.lg, gap: 2 },
});
